import Day from '@awesomecomponents/day-js';
import {
    BREAKPOINTS,
    Button,
    ButtonSelector,
    Checkbox,
    CheckboxGroup,
    DateOfBirthExperimental,
    DatePicker,
    Dropdown,
    ExpandablePanel,
    H2,
    MessageCard,
    Modal,
    TextInput,
    Tooltip,
    useMedia,
} from '@manulife/mux';
import { useFormik } from 'formik';
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import useAEMContent, { FRAGMENT_MP } from '../../CustomHooks/useAEMContent';
import useShallowEqualSelector from '../../CustomHooks/useShallowEqualSelector';
import { APP_ROUTES, DATE_FORMAT } from '../../utils/AppConstants';
import { GetFamily } from '../App/ReducerActions/GetFamily';
import { PurgeApplicationStateAndContext } from '../App/ReducerActions/PurgeApplicationStateAndContext';
import AddFamilyModalLoadingIndicator from '../LoadingIndicator/AddFamilyModalLoadingIndicator';
import {
    ButtonWrapper,
    ErrorMsg,
    FamilyCheckboxWrapper,
    InfoMessageWrapper,
    PaddedFields,
    TextInputWrapper,
    ValidationAlertWrapper,
} from '../Shared/SharedStyles';
import { AddFamilyMember } from './ReducerActions/AddFamilyMember';
import { getAddFamilyValidationSchema } from './Validations';
import benefitCardEN from './assets/benefitCardEN.svg';
import benefitCardFR from './assets/benefitCardFR.svg';
import { canAddFamily, cobBenefitsToDisplay, filteredCobBenefits, getCoverageDate } from './utils';

const DEFAULT_FAMILY_MEMBER = {
    firstName: '',
    lastName: '',
    // Mux component Date of birth expects object as a default value.
    dateOfBirth: {
        day: '',
        year: '',
    },
    gender: '',
    familyMemberType: '',
    additionReason: '',
    effectiveDate: '',
    hasOtherCoverage: '',
    cobProvider: '',
    cobContractNumber: '',
    cobCertificateNumber: '',
    // Mux component CheckboxGroup expects object as a default value.
    kindOfCoverageSelected: {},
    familyMemberAccuracyAgreement: false,
};

const AddFamilyModal = () => {
    const {
        isLoadingFeatures,
        isLoadingMemberData,
        isLoadingSupplementalMemberData,
        isLoadingFamilyData,
        isLoadingMemberBenefitData,
        features,
        language,
        allowedDependentActions,
        memberBenefits,
        memberEffectiveDate,
        coverageAppliedDate,
        dependents,
        isAddFamilyDataSaving,
        errorUpdate,
        studentMaxAge,
        isCsr,
    } = useShallowEqualSelector((state) => ({
        isLoadingFeatures: state.Features.isLoading,
        isLoadingMemberData: state.MemberData.isLoadingMemberData,
        isLoadingSupplementalMemberData: state.MemberData.isLoadingSupplementalMemberData,
        isLoadingFamilyData: state.MemberData.isLoadingFamilyData,
        isLoadingMemberBenefitData: state.MemberData.isLoadingMemberBenefitData,
        features: state.Features.data,
        language: state.Authentication.language,
        allowedDependentActions: state.MemberData.allowedDependentActions,
        memberBenefits: state.MemberData.memberBenefits,
        memberEffectiveDate: state.MemberData.member.effectiveDate,
        coverageAppliedDate: state.MemberData.member.coverageAppliedDate,
        dependents: state.MemberData.familyMembers,
        isAddFamilyDataSaving: state.MemberData.isAddFamilyDataSaving,
        errorUpdate: state.Errors.familyMemberUpdate,
        studentMaxAge: state.MemberData.member.plan?.studentMaxAge,
        isCsr: state.Authentication.isCsr,
    }));

    const history = useHistory();
    const dispatch = useDispatch();
    const errorRef = useRef();

    const benefitCardImage = language === 'fr-CA' ? benefitCardFR : benefitCardEN;
    const mobileView = useMedia([`(max-width: ${BREAKPOINTS.TABLET_PORTRAIT}px)`], [true], false);
    const customFieldStyle = mobileView ? { marginTop: '20px', width: '100%' } : { marginTop: '20px' };

    const cancelButton = useAEMContent(FRAGMENT_MP.common, 'cancelButton');
    const saveButton = useAEMContent(FRAGMENT_MP.common, 'saveButton');
    const toolTipButton = useAEMContent(FRAGMENT_MP.common, 'ariaTooltip');

    const addFamilyMemberHeader = useAEMContent(FRAGMENT_MP.familyMember, 'addFamilyMemberHeader');
    const addFamilyMemberMessage = useAEMContent(FRAGMENT_MP.familyMember, 'addFamilyMemberMessage', {
        parseHTML: true,
    });
    const firstNameLabel = useAEMContent(FRAGMENT_MP.familyMember, 'firstNameLabel');
    const lastNameLabel = useAEMContent(FRAGMENT_MP.familyMember, 'lastNameLabel');
    const sex = useAEMContent(FRAGMENT_MP.familyMember, 'sex');
    const sexTooltip = useAEMContent(FRAGMENT_MP.familyMember, 'sexTooltip', { parseHTML: true });
    const maleLabel = useAEMContent(FRAGMENT_MP.familyMember, 'maleLabel');
    const femaleLabel = useAEMContent(FRAGMENT_MP.familyMember, 'femaleLabel');
    const dropDownPlaceholder = useAEMContent(FRAGMENT_MP.familyMember, 'dropDownPlaceholder');
    const relationshipLabel = useAEMContent(FRAGMENT_MP.familyMember, 'relationshipLabel');
    const childLabel = useAEMContent(FRAGMENT_MP.familyMember, 'childLabel');
    const spouseLabel = useAEMContent(FRAGMENT_MP.familyMember, 'spouseLabel');
    const reasonForAdditionLabel = useAEMContent(FRAGMENT_MP.familyMember, 'reasonForAdditionLabel');
    const reasonJoiningMyNewPlan = useAEMContent(FRAGMENT_MP.familyMember, 'reasonJoiningMyNewPlan');
    const reasonBirth = useAEMContent(FRAGMENT_MP.familyMember, 'reasonBirth');
    const reasonAdoption = useAEMContent(FRAGMENT_MP.familyMember, 'reasonAdoption');
    const reasonSpousesChild = useAEMContent(FRAGMENT_MP.familyMember, 'reasonSpousesChild');
    const reasonRegainingCustody = useAEMContent(FRAGMENT_MP.familyMember, 'ReasonRegainingCustody');
    const reasonOverageChild = useAEMContent(FRAGMENT_MP.familyMember, 'ReasonOverageChild');
    const reasonChangeInSpouseCoverage = useAEMContent(FRAGMENT_MP.familyMember, 'ReasonChangeInSpouseCoverage');
    const reasonMaritialStatusChange = useAEMContent(FRAGMENT_MP.familyMember, 'ReasonMaritialStatusChange');
    const coverageStartDateLabel = useAEMContent(FRAGMENT_MP.familyMember, 'coverageStartDateLabel');
    const dateLabelJoiningMyNewPlan = useAEMContent(FRAGMENT_MP.familyMember, 'dateLabelJoiningMyNewPlan');
    const dateLabelSpousesChild = useAEMContent(FRAGMENT_MP.familyMember, 'dateLabelSpousesChild');
    const dateLabelOverAgeChildReturningToSchool = useAEMContent(
        FRAGMENT_MP.familyMember,
        'dateLabelOverAgeChildReturningToSchool'
    );
    const cobLabel = useAEMContent(FRAGMENT_MP.familyMember, 'cobLabel');
    const cobToolTip = useAEMContent(FRAGMENT_MP.familyMember, 'cobToolTip', { parseHTML: true });
    const declarationMessage = useAEMContent(FRAGMENT_MP.familyMember, 'declarationMessage', { parseHTML: true });
    const cobYes = useAEMContent(FRAGMENT_MP.familyMember, 'cobYes');
    const cobNo = useAEMContent(FRAGMENT_MP.familyMember, 'cobNo');
    const cobType = useAEMContent(FRAGMENT_MP.familyMember, 'cobType');
    const providerType = useAEMContent(FRAGMENT_MP.familyMember, 'providerType');
    const benefitCardHeaderLabel = useAEMContent(FRAGMENT_MP.familyMember, 'benefitCardHeader');
    const benefitCardInstruction = useAEMContent(FRAGMENT_MP.familyMember, 'benefitCardInstructions');
    const providerManulife = useAEMContent(FRAGMENT_MP.familyMember, 'ProviderManulife');
    const providerOther = useAEMContent(FRAGMENT_MP.familyMember, 'ProviderOther');
    const contractNumber = useAEMContent(FRAGMENT_MP.familyMember, 'contractNumber');
    const certificateNumber = useAEMContent(FRAGMENT_MP.familyMember, 'certificateNumber');
    const coverageDateTooltip = useAEMContent(FRAGMENT_MP.familyMember, 'coverageDateTooltip', { parseHTML: true });

    const validationMessages = {
        errorSummaryTitle: useAEMContent(FRAGMENT_MP.validationMessages, 'errorSummaryTitle'),
        errorSummaryMessage: useAEMContent(FRAGMENT_MP.validationMessages, 'errorSummaryMessage'),
        firstNameBlank: useAEMContent(FRAGMENT_MP.validationMessages, 'firstNameBlank'),
        lastNameblank: useAEMContent(FRAGMENT_MP.validationMessages, 'lastNameblank'),
        dateOfBirthBlank: useAEMContent(FRAGMENT_MP.validationMessages, 'dateOfBirthBlank'),
        sexBlank: useAEMContent(FRAGMENT_MP.validationMessages, 'sexBlank'),
        relationshipBlank: useAEMContent(FRAGMENT_MP.validationMessages, 'relationshipBlank'),
        reasonForAdditionBlank: useAEMContent(FRAGMENT_MP.validationMessages, 'reasonForAdditionBlank'),
        missingCoverageStartDate: useAEMContent(FRAGMENT_MP.validationMessages, 'missingCoverageStartDate'),
        errorOverSixtyDays: useAEMContent(FRAGMENT_MP.validationMessages, 'errorOverSixtyDays'),
        errorOverThirtyOneDays: useAEMContent(FRAGMENT_MP.validationMessages, 'errorOverThirtyOneDays'),
        cobProviderBlank: useAEMContent(FRAGMENT_MP.validationMessages, 'cobProviderBlank'),
        cobPolicyBlank: useAEMContent(FRAGMENT_MP.validationMessages, 'cobPolicyBlank'),
        cobCertBlank: useAEMContent(FRAGMENT_MP.validationMessages, 'cobCertBlank'),
        cobBenefitBlank: useAEMContent(FRAGMENT_MP.validationMessages, 'cobBenefitBlank'),
        cobCoverageSelectBlank: useAEMContent(FRAGMENT_MP.validationMessages, 'cobCoverageSelectBlank'),
        certifyCheckBox: useAEMContent(FRAGMENT_MP.validationMessages, 'certifyCheckBox'),
        duplicateActiveDependent: useAEMContent(FRAGMENT_MP.validationMessages, 'duplicateChild'),
        duplicateTerminatedDependent: useAEMContent(FRAGMENT_MP.validationMessages, 'addTerminatedFamilyMemberError'),
        overageDependent: useAEMContent(FRAGMENT_MP.validationMessages, 'overageDependent'),
    };

    const isDataLoading =
        isLoadingFeatures ||
        isLoadingMemberData ||
        isLoadingSupplementalMemberData ||
        isLoadingFamilyData ||
        isLoadingMemberBenefitData;
    const {
        handleSubmit,
        values,
        touched,
        errors,
        isValid,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        submitCount,
    } = useFormik({
        initialValues: {
            ...DEFAULT_FAMILY_MEMBER,
        },
        enableReinitialize: true,
        onSubmit: async () => {
            let coverageEffectiveDate;
            if (values.additionReason === 'JoiningMyNewPlan' || values.additionReason === 'Birth') {
                const dateOfBirthISO = new Day(values.dateOfBirth.date).format('en', DATE_FORMAT.ISO_STANDARD);
                coverageEffectiveDate = getCoverageDate(memberEffectiveDate, coverageAppliedDate, dateOfBirthISO);
            } else {
                const dateInputted = Day.parse(values.effectiveDate, DATE_FORMAT.MUX_LOCALIZED).format(
                    'en',
                    DATE_FORMAT.ISO_STANDARD
                );
                coverageEffectiveDate = getCoverageDate(memberEffectiveDate, coverageAppliedDate, dateInputted);
            }

            const familyMemberToAdd = {
                ...values,
                effectiveDate: coverageEffectiveDate,
            };
            const response = await dispatch(AddFamilyMember(familyMemberToAdd, cobBenefits));
            if (response.success) {
                dispatch(GetFamily());
                history.push(APP_ROUTES.familyTab);
            } else {
                await dispatch(PurgeApplicationStateAndContext());
            }
        },
        validationSchema: Yup.object().shape(
            getAddFamilyValidationSchema(validationMessages, dependents, studentMaxAge)
        ),
    });

    const cobBenefits = useMemo(
        () => !isDataLoading && filteredCobBenefits(memberBenefits),
        [isDataLoading, memberBenefits]
    );

    const dateLabel = useMemo(() => {
        switch (values.additionReason) {
            case 'JoiningMyNewPlan':
                return dateLabelJoiningMyNewPlan;
            case 'SpousesChild':
                return dateLabelSpousesChild;
            case 'OverAgeChildReturningToSchool':
                return dateLabelOverAgeChildReturningToSchool;
            default:
                return coverageStartDateLabel;
        }
    }, [
        values.additionReason,
        coverageStartDateLabel,
        dateLabelJoiningMyNewPlan,
        dateLabelOverAgeChildReturningToSchool,
        dateLabelSpousesChild,
    ]);

    const displayedCobBenefits = useMemo(
        () => !isDataLoading && cobBenefitsToDisplay(memberBenefits),
        [isDataLoading, memberBenefits]
    );

    // Checks if the active spouse exists because spouse checkbox is not shown if member already have active spouse.
    const doesSpouseExist = useMemo(
        () =>
            dependents.some(
                (familyMember) => familyMember.status === 'ACTIVE' && familyMember.familyMemberType === 'SPOUSE'
            ),
        [dependents]
    );

    // Filter the benefits down to only have benefits with coverage type SINGLE_PLUS_MORE
    const singlePlusMoreCoverage = useMemo(
        () =>
            !isDataLoading &&
            memberBenefits.filter(
                (benefit) => ['EHC', 'DEN'].includes(benefit.code) && benefit.coverageType === 'SINGLE_PLUS_MORE'
            ),
        [isDataLoading, memberBenefits]
    );

    // Gets error messages to display in message card alert.
    const errorMessage = useMemo(() => {
        if (errors.duplicateChild) {
            return validationMessages.duplicateActiveDependent;
        } else if (errors.terminatedDependent) {
            return validationMessages.duplicateTerminatedDependent;
        } else if (errors.overageDependent) {
            return validationMessages.overageDependent;
        } else {
            return validationMessages.errorSummaryMessage;
        }
    }, [
        errors,
        validationMessages.errorSummaryMessage,
        validationMessages.duplicateActiveDependent,
        validationMessages.duplicateTerminatedDependent,
        validationMessages.overageDependent,
    ]);

    // Check to see if member is allowed to add family members
    const manageLifeEventOnMmb = features['lifeEvent'] || features['lifeEventHold'];

    const allowedToAddFamily = useMemo(
        () =>
            !isDataLoading &&
            !manageLifeEventOnMmb &&
            canAddFamily(allowedDependentActions, features, dependents, memberBenefits),
        [isDataLoading, manageLifeEventOnMmb, allowedDependentActions, features, dependents, memberBenefits]
    );

    // Check to see if modal should be disabled
    const disableModal = useMemo(
        () => isAddFamilyDataSaving || !allowedToAddFamily,
        [isAddFamilyDataSaving, allowedToAddFamily]
    );

    // Scrolls to error messages at the top of the modal on submit.
    useEffect(() => {
        if (!isValid && isSubmitting) {
            errorRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start',
            });
        }
    }, [isValid, isSubmitting]);

    // Reset the addition reasons and effective date when changing family member type.
    useEffect(() => {
        setFieldValue('additionReason', DEFAULT_FAMILY_MEMBER.additionReason);
        setFieldValue('effectiveDate', DEFAULT_FAMILY_MEMBER.effectiveDate);
    }, [values.familyMemberType, setFieldValue]);

    return (
        <Modal
            id="addFamilyMemberModal"
            aria-label="Add a family member"
            isOpen={true}
            onClose={() => history.push(APP_ROUTES.familyTab)}
        >
            {isDataLoading && <AddFamilyModalLoadingIndicator data-testid="addFamilyModalLoadingIndicator" />}
            {!isDataLoading && (
                <form onSubmit={handleSubmit}>
                    <H2>{addFamilyMemberHeader}</H2>
                    <InfoMessageWrapper>
                        <MessageCard variant={MessageCard.VARIANT.SECONDARY_INFORMATION}>
                            {addFamilyMemberMessage}
                        </MessageCard>
                    </InfoMessageWrapper>

                    <ValidationAlertWrapper
                        id="addFamilyMemberErrors"
                        ref={errorRef}
                        aria-hidden={isValid}
                        aria-live="polite"
                        aria-atomic="true"
                        role="alert"
                    >
                        {errorUpdate?.hasError && (
                            <MessageCard
                                variant={MessageCard.VARIANT.PRIMARY_ALERT}
                                titleText={validationMessages.errorSummaryTitle}
                            >
                                Problem: {errorUpdate.message} Code: {errorUpdate.code}
                            </MessageCard>
                        )}

                        {!isValid && submitCount > 0 && (
                            <MessageCard
                                variant={MessageCard.VARIANT.PRIMARY_ALERT}
                                titleText={validationMessages.errorSummaryTitle}
                            >
                                {errorMessage}
                            </MessageCard>
                        )}
                    </ValidationAlertWrapper>
                    <TextInputWrapper>
                        <TextInput
                            id="firstName"
                            label={firstNameLabel}
                            customStyle={{
                                rootStyle: {
                                    width: '100%',
                                },
                            }}
                            value={values.firstName}
                            onChange={(newValue) => setFieldValue('firstName', newValue)}
                            onBlur={() => setFieldTouched('firstName', true)}
                            errors={
                                touched.firstName && [
                                    {
                                        id: 'firstName',
                                        msg: errors.firstName,
                                        isError: Boolean(errors.firstName),
                                    },
                                ]
                            }
                            disabled={disableModal}
                            maxLength={30}
                            callouts
                            ariaRequired
                        />
                        <TextInput
                            id="lastName"
                            label={lastNameLabel}
                            customStyle={{
                                rootStyle: {
                                    width: '100%',
                                },
                            }}
                            value={values.lastName}
                            onChange={(newValue) => setFieldValue('lastName', newValue)}
                            onBlur={() => setFieldTouched('lastName', true)}
                            errors={
                                touched.lastName && [
                                    {
                                        id: 'lastName',
                                        msg: errors.lastName,
                                        isError: Boolean(errors.lastName),
                                    },
                                ]
                            }
                            disabled={disableModal}
                            maxLength={30}
                            callouts
                            ariaRequired
                        />
                    </TextInputWrapper>
                    <DateOfBirthExperimental
                        id="dateOfBirth"
                        value={values.dateOfBirth}
                        customStyle={{ fieldSetStyle: customFieldStyle }}
                        disabledDays={{
                            after: new Date(Day.TODAY.toDate()),
                            before: new Date(new Day([1900, 0, 1]).toDate()),
                        }}
                        onChange={(newValue) => setFieldValue('dateOfBirth', newValue)}
                        errors={
                            (touched.dateOfBirth?.day || touched.dateOfBirth?.month || touched.dateOfBirth?.year) && [
                                {
                                    id: 'dateOfBirth.date',
                                    msg: errors.dateOfBirth?.date,
                                    isError: Boolean(errors.dateOfBirth?.date),
                                },
                            ]
                        }
                        disabled={disableModal}
                        callouts
                        required={true}
                    />
                    <ButtonSelector
                        id="gender"
                        label={
                            <>
                                {sex}
                                <Tooltip
                                    id="genderTooltip"
                                    ariaLabel={`${sex} ${toolTipButton}`}
                                    customStyle={{
                                        rootStyle: { marginLeft: '10px' },
                                    }}
                                    content={sexTooltip}
                                    bubbleWidth={200}
                                    variant={Tooltip.VARIANT.SECONDARY}
                                ></Tooltip>
                            </>
                        }
                        values={[
                            { label: maleLabel, value: 'MALE' },
                            {
                                label: femaleLabel,
                                value: 'FEMALE',
                            },
                        ]}
                        value={values.gender}
                        customStyle={{ rootStyle: { marginTop: '20px' } }}
                        onChange={(newValue) => setFieldValue('gender', newValue)}
                        errors={
                            touched.gender && [
                                {
                                    id: 'gender',
                                    msg: errors.gender,
                                    isError: Boolean(errors.gender),
                                },
                            ]
                        }
                        verticalStackingBreakPoint={0}
                        disabled={disableModal}
                        ariaRequired
                    ></ButtonSelector>
                    <PaddedFields>
                        <Dropdown
                            id="familyMemberType"
                            label={relationshipLabel}
                            dropdownItems={[
                                allowedDependentActions.addChild && {
                                    label: childLabel,
                                    value: 'CHILD',
                                },
                                allowedDependentActions.addSpouse &&
                                    !doesSpouseExist &&
                                    singlePlusMoreCoverage[0]?.coverageType !== 'SINGLE_PLUS_MORE' && {
                                        label: spouseLabel,
                                        value: 'SPOUSE',
                                    },
                            ].filter((item) => item)}
                            customStyle={{ rootStyle: customFieldStyle }}
                            value={values.familyMemberType}
                            placeholder={dropDownPlaceholder}
                            onChange={(newValue) => {
                                delete touched.additionReason;
                                delete touched.effectiveDate;
                                setFieldValue('familyMemberType', newValue);
                            }}
                            errors={
                                touched.familyMemberType && [
                                    {
                                        id: 'familyMemberType',
                                        msg: errors.familyMemberType,
                                        isError: Boolean(errors.familyMemberType),
                                    },
                                ]
                            }
                            disabled={disableModal}
                            callouts
                            ariaRequired
                            data-testid="add_fam_familyMemberType"
                        />
                    </PaddedFields>
                    <Dropdown
                        id="additionReason"
                        label={reasonForAdditionLabel}
                        dropdownItems={[
                            (values.familyMemberType === 'CHILD' || values.familyMemberType === 'SPOUSE') && {
                                label: reasonJoiningMyNewPlan,
                                value: 'JoiningMyNewPlan',
                            },
                            values.familyMemberType === 'CHILD' && {
                                label: reasonBirth,
                                value: 'Birth',
                            },
                            values.familyMemberType === 'CHILD' && {
                                label: reasonAdoption,
                                value: 'Adoption',
                            },
                            values.familyMemberType === 'CHILD' && {
                                label: reasonSpousesChild,
                                value: 'SpousesChild',
                            },
                            values.familyMemberType === 'CHILD' && {
                                label: reasonRegainingCustody,
                                value: 'RegainingCustodyOfChild',
                            },
                            values.familyMemberType === 'CHILD' && {
                                label: reasonOverageChild,
                                value: 'OverAgeChildReturningToSchool',
                            },
                            !doesSpouseExist &&
                                values.familyMemberType === 'SPOUSE' && {
                                    label: reasonChangeInSpouseCoverage,
                                    value: 'ChangeInSpouseCoverage',
                                },
                            !doesSpouseExist &&
                                values.familyMemberType === 'SPOUSE' && {
                                    label: reasonMaritialStatusChange,
                                    value: 'MaritialStatusChange',
                                },
                        ].filter((item) => item)}
                        customStyle={{ rootStyle: customFieldStyle }}
                        value={values.additionReason}
                        placeholder={dropDownPlaceholder}
                        onChange={(newValue) => setFieldValue('additionReason', newValue)}
                        errors={
                            touched.additionReason && [
                                {
                                    id: 'additionReason',
                                    msg: errors.additionReason,
                                    isError: Boolean(errors.additionReason),
                                },
                            ]
                        }
                        disabled={disableModal}
                        callouts
                        ariaRequired
                    />
                    {values.additionReason !== 'JoiningMyNewPlan' && values.additionReason !== 'Birth' && (
                        <DatePicker
                            id="effectiveDate"
                            value={values.effectiveDate}
                            label={dateLabel}
                            tooltip={
                                <Tooltip
                                    id="effectiveDateTooltip"
                                    ariaLabel={`${dateLabel} ${toolTipButton}`}
                                    customStyle={{
                                        rootStyle: { marginLeft: '10px' },
                                    }}
                                    content={coverageDateTooltip}
                                    bubbleWidth={200}
                                    variant={Tooltip.VARIANT.SECONDARY}
                                ></Tooltip>
                            }
                            toggleAriaLabel={dateLabel}
                            customStyle={{ rootStyle: customFieldStyle }}
                            disabledDays={{
                                after: new Date(),
                                before: new Date(1900, 0, 1),
                            }}
                            onChange={(newValue) => setFieldValue('effectiveDate', newValue)}
                            errors={
                                touched.effectiveDate && values?.effectiveDate.length > 6
                                    ? [
                                          {
                                              id: 'effectiveDate',
                                              msg: errors.effectiveDate,
                                              isError: Boolean(errors.effectiveDate),
                                          },
                                      ]
                                    : []
                            }
                            disabled={disableModal}
                            errorCallout
                            ariaRequired
                        />
                    )}
                    <ButtonSelector
                        id="hasOtherCoverage"
                        label={
                            <>
                                {cobLabel}
                                <Tooltip
                                    id="hasOtherCoverageTooltip"
                                    ariaLabel={`${cobLabel} ${toolTipButton}`}
                                    customStyle={{
                                        rootStyle: { marginLeft: '10px' },
                                    }}
                                    content={cobToolTip}
                                    bubbleWidth={200}
                                    variant={Tooltip.VARIANT.SECONDARY}
                                    side={Tooltip.SIDES.TOP}
                                ></Tooltip>
                            </>
                        }
                        values={[
                            {
                                label: cobYes,
                                value: 'true',
                            },
                            {
                                label: cobNo,
                                value: 'false',
                            },
                        ]}
                        customStyle={{ rootStyle: { marginTop: '20px' } }}
                        value={values.hasOtherCoverage.toString()}
                        onChange={(newValue) => {
                            setFieldValue('hasOtherCoverage', newValue === 'true');
                        }}
                        errors={
                            touched.hasOtherCoverage && [
                                {
                                    id: 'hasOtherCoverage',
                                    msg: errors.hasOtherCoverage,
                                    isError: Boolean(errors.hasOtherCoverage),
                                },
                            ]
                        }
                        verticalStackingBreakPoint={0}
                        disabled={disableModal}
                    ></ButtonSelector>
                    <div>
                        {values.hasOtherCoverage && (
                            <>
                                <CheckboxGroup
                                    direction="column"
                                    id="kindOfCoverageSelected"
                                    legend={cobType}
                                    values={displayedCobBenefits}
                                    value={values.kindOfCoverageSelected}
                                    customStyle={{
                                        legendStyle: {
                                            marginLeft: '-10px',
                                        },
                                        checkboxStyle: {
                                            marginLeft: '-8px',
                                        },
                                    }}
                                    onChange={(newValue) => {
                                        setFieldValue('kindOfCoverageSelected', newValue);
                                    }}
                                    selected={values.kindOfCoverageSelected}
                                    errors={
                                        touched.kindOfCoverageSelected && [
                                            {
                                                id: 'kindOfCoverageSelected',
                                                msg: errors.kindOfCoverageSelected,
                                                isError: Boolean(errors.kindOfCoverageSelected),
                                            },
                                        ]
                                    }
                                    disabled={disableModal}
                                    errorCallout
                                />
                                <Dropdown
                                    id="cobProvider"
                                    label={providerType}
                                    customStyle={{ rootStyle: customFieldStyle }}
                                    dropdownItems={[
                                        {
                                            label: providerManulife,
                                            value: 'MANULIFE',
                                        },
                                        {
                                            label: providerOther,
                                            value: 'OTHER',
                                        },
                                    ]}
                                    placeholder={dropDownPlaceholder}
                                    onChange={(newValue) => setFieldValue('cobProvider', newValue)}
                                    value={values.cobProvider}
                                    errors={
                                        touched.cobProvider && [
                                            {
                                                id: 'cobProvider',
                                                msg: errors.cobProvider,
                                                isError: Boolean(errors.cobProvider),
                                            },
                                        ]
                                    }
                                    disabled={disableModal}
                                    callouts
                                    ariaRequired
                                />
                            </>
                        )}

                        {values.hasOtherCoverage && values.cobProvider === 'MANULIFE' && (
                            <>
                                <ExpandablePanel
                                    id={'benefitsCardSummaryExpandablePanel'}
                                    title={benefitCardHeaderLabel}
                                    customStyle={{ expandablePanelStyle: { marginTop: '20px', marginBottom: '20px' } }}
                                >
                                    {benefitCardInstruction}
                                    <img
                                        src={benefitCardImage}
                                        alt="benefit card"
                                        style={{ maxWidth: '100%', display: 'block' }}
                                    />
                                </ExpandablePanel>
                                <TextInput
                                    id="cobContractNumber"
                                    label={contractNumber}
                                    customStyle={{ rootStyle: customFieldStyle }}
                                    value={values.cobContractNumber}
                                    onChange={(newValue) => setFieldValue('cobContractNumber', newValue)}
                                    onBlur={() => setFieldTouched('cobContractNumber', true)}
                                    errors={
                                        touched.cobContractNumber && [
                                            {
                                                id: 'cobContractNumber',
                                                msg: errors.cobContractNumber,
                                                isError: Boolean(errors.cobContractNumber),
                                            },
                                        ]
                                    }
                                    disabled={disableModal}
                                    maxLength={7}
                                    callouts
                                    ariaRequired
                                />
                                <TextInput
                                    id="cobCertificateNumber"
                                    label={certificateNumber}
                                    customStyle={{ rootStyle: customFieldStyle }}
                                    value={values.cobCertificateNumber}
                                    onChange={(newValue) => setFieldValue('cobCertificateNumber', newValue)}
                                    onBlur={() => setFieldTouched('cobCertificateNumber', true)}
                                    errors={
                                        touched.cobCertificateNumber && [
                                            {
                                                id: 'cobCertificateNumber',
                                                msg: errors.cobCertificateNumber,
                                                isError: Boolean(errors.cobCertificateNumber),
                                            },
                                        ]
                                    }
                                    disabled={disableModal}
                                    maxLength={9}
                                    forceUpper={true}
                                    callouts
                                    ariaRequired
                                />
                            </>
                        )}
                    </div>
                    <FamilyCheckboxWrapper>
                        <Checkbox
                            id="familyMemberAccuracyAgreement"
                            checked={values.familyMemberAccuracyAgreement}
                            onChange={(newValue) => setFieldValue('familyMemberAccuracyAgreement', newValue)}
                            error={
                                Boolean(errors.familyMemberAccuracyAgreement) && touched.familyMemberAccuracyAgreement
                            }
                            disabled={disableModal}
                            aria-required="true"
                        />
                        <label htmlFor="familyMemberAccuracyAgreement">
                            {declarationMessage}
                            {Boolean(errors.familyMemberAccuracyAgreement) && touched.familyMemberAccuracyAgreement && (
                                <ErrorMsg aria-atomic="true" aria-live="polite" role="alert">
                                    {errors.familyMemberAccuracyAgreement}
                                </ErrorMsg>
                            )}
                        </label>
                    </FamilyCheckboxWrapper>
                    <ButtonWrapper>
                        <Button
                            id="cancelAddFamilyButton"
                            variant={Button.VARIANT.SECONDARY}
                            onClick={() => history.push(APP_ROUTES.familyTab)}
                            disabled={isAddFamilyDataSaving}
                        >
                            {cancelButton}
                        </Button>
                        <Button
                            id="updateAddFamilyButton"
                            type="submit"
                            saving={isAddFamilyDataSaving}
                            disabled={disableModal || isCsr}
                        >
                            {saveButton}
                        </Button>
                    </ButtonWrapper>
                </form>
            )}
        </Modal>
    );
};

export default AddFamilyModal;
